import React, { useEffect, useState } from 'react';
import { settingsApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
    Building2, 
    Upload, 
    CreditCard, 
    FileText, 
    Save,
    Phone,
    Mail,
    MapPin,
    Image,
    User
} from 'lucide-react';

export default function SettingsPage() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [previewing, setPreviewing] = useState(false);
    const [settings, setSettings] = useState({
        company_name: '',
        company_tagline: '',
        siren: '',
        address: '',
        phone: '',
        email: '',
        logo_base64: '',
        iban: '',
        bic: '',
        bank_name: '',
        account_holder: '',
        legal_mentions_quote: '',
        legal_mentions_invoice: '',
    });

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            const response = await settingsApi.get();
            setSettings(response.data);
        } catch (error) {
            toast.error('Erreur lors du chargement des paramètres');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await settingsApi.update(settings);
            toast.success('Paramètres enregistrés');
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (file.size > 5 * 1024 * 1024) {
            toast.error('Le fichier est trop volumineux (max 5 MB)');
            return;
        }

        try {
            const response = await settingsApi.uploadLogo(file);
            setSettings({ ...settings, logo_base64: response.data.logo });
            toast.success('Logo uploadé');
        } catch (error) {
            toast.error("Erreur lors de l'upload du logo");
        }
    };

    const handlePreviewQuote = async () => {
        const previewWindow = window.open('', '_blank');
        if (!previewWindow) {
            toast.error('Popup bloquée: autorisez les popups pour prévisualiser le devis');
            return;
        }
        previewWindow.opener = null;

        previewWindow.document.write('<p style="font-family: sans-serif; padding: 12px;">Génération de la prévisualisation...</p>');
        setPreviewing(true);
        try {
            const response = await settingsApi.previewQuote(settings);
            const pdfUrl = window.URL.createObjectURL(response.data);
            previewWindow.location.href = pdfUrl;
            setTimeout(() => window.URL.revokeObjectURL(pdfUrl), 60000);
        } catch (error) {
            previewWindow.close();
            toast.error('Erreur lors de la prévisualisation du devis');
        } finally {
            setPreviewing(false);
        }
    };

    if (loading) {
        return (
            <div className="p-8">
                <div className="space-y-6">
                    {[...Array(3)].map((_, i) => (
                        <div key={i} className="stat-card h-48 skeleton" />
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="p-6 md:p-8 space-y-8" data-testid="settings-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold text-[#f8fafc]">Paramètres</h1>
                    <p className="text-[#94a3b8] mt-1">Configurez votre entreprise</p>
                </div>
                <div className="flex flex-col sm:flex-row gap-2">
                    <Button
                        onClick={handlePreviewQuote}
                        disabled={previewing}
                        data-testid="preview-quote-btn"
                        variant="outline"
                        className="border-[#1e293b] text-[#94a3b8] hover:bg-[#1e293b] hover:text-[#f8fafc]"
                    >
                        <FileText className="w-5 h-5 mr-2" />
                        {previewing ? 'Prévisualisation...' : 'Prévisualiser le devis'}
                    </Button>
                    <Button 
                        onClick={handleSave}
                        disabled={saving}
                        data-testid="save-settings-btn"
                        className="bg-[#d4af37] text-[#020617] hover:bg-[#fcd34d] font-semibold"
                    >
                        <Save className="w-5 h-5 mr-2" />
                        {saving ? 'Enregistrement...' : 'Enregistrer'}
                    </Button>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Company Identity */}
                <div className="stat-card">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 rounded-lg bg-[#d4af37]/10">
                            <Building2 className="w-5 h-5 text-[#d4af37]" />
                        </div>
                        <h2 className="text-xl font-semibold text-[#f8fafc]">Identité</h2>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <Label className="text-[#94a3b8]">Nom de l'entreprise</Label>
                            <div className="relative mt-1">
                                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
                                <Input
                                    data-testid="company-name-input"
                                    value={settings.company_name}
                                    onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                                    placeholder="Ma Société Traiteur"
                                    className="pl-10 bg-[#020617] border-[#1e293b] text-[#f8fafc]"
                                />
                            </div>
                        </div>

                        <div>
                            <Label className="text-[#94a3b8]">Slogan / Tagline</Label>
                            <Input
                                data-testid="company-tagline-input"
                                value={settings.company_tagline}
                                onChange={(e) => setSettings({ ...settings, company_tagline: e.target.value })}
                                placeholder="L'excellence culinaire à votre service"
                                className="mt-1 bg-[#020617] border-[#1e293b] text-[#f8fafc]"
                            />
                        </div>

                        <div>
                            <Label className="text-[#94a3b8]">SIREN</Label>
                            <Input
                                data-testid="company-siren-input"
                                value={settings.siren || ''}
                                onChange={(e) => setSettings({ ...settings, siren: e.target.value })}
                                placeholder="123 456 789"
                                className="mt-1 bg-[#020617] border-[#1e293b] text-[#f8fafc] mono"
                            />
                        </div>

                        <div>
                            <Label className="text-[#94a3b8]">Adresse</Label>
                            <div className="relative mt-1">
                                <MapPin className="absolute left-3 top-3 w-4 h-4 text-[#475569]" />
                                <Textarea
                                    data-testid="company-address-input"
                                    value={settings.address}
                                    onChange={(e) => setSettings({ ...settings, address: e.target.value })}
                                    placeholder="123 Rue de la Gastronomie&#10;75001 Paris"
                                    className="pl-10 bg-[#020617] border-[#1e293b] text-[#f8fafc] min-h-[80px]"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-[#94a3b8]">Téléphone</Label>
                                <div className="relative mt-1">
                                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
                                    <Input
                                        data-testid="company-phone-input"
                                        value={settings.phone}
                                        onChange={(e) => setSettings({ ...settings, phone: e.target.value })}
                                        placeholder="01 23 45 67 89"
                                        className="pl-10 bg-[#020617] border-[#1e293b] text-[#f8fafc]"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="text-[#94a3b8]">Email</Label>
                                <div className="relative mt-1">
                                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
                                    <Input
                                        data-testid="company-email-input"
                                        type="email"
                                        value={settings.email}
                                        onChange={(e) => setSettings({ ...settings, email: e.target.value })}
                                        placeholder="contact@traiteur.fr"
                                        className="pl-10 bg-[#020617] border-[#1e293b] text-[#f8fafc]"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Logo Upload */}
                <div className="stat-card">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 rounded-lg bg-purple-400/10">
                            <Image className="w-5 h-5 text-purple-400" />
                        </div>
                        <h2 className="text-xl font-semibold text-[#f8fafc]">Logo</h2>
                    </div>

                    <div className="flex flex-col items-center">
                        {settings.logo_base64 ? (
                            <div className="w-48 h-48 rounded-lg bg-[#020617] border border-[#1e293b] flex items-center justify-center overflow-hidden mb-4">
                                <img 
                                    src={settings.logo_base64} 
                                    alt="Logo" 
                                    className="max-w-full max-h-full object-contain"
                                    data-testid="company-logo-preview"
                                />
                            </div>
                        ) : (
                            <div className="w-48 h-48 rounded-lg bg-[#020617] border-2 border-dashed border-[#1e293b] flex flex-col items-center justify-center mb-4">
                                <Upload className="w-10 h-10 text-[#475569] mb-2" />
                                <p className="text-[#64748b] text-sm">Aucun logo</p>
                            </div>
                        )}
                        
                        <label className="cursor-pointer">
                            <input
                                type="file"
                                accept="image/*"
                                onChange={handleLogoUpload}
                                className="hidden"
                                data-testid="logo-upload-input"
                            />
                            <Button variant="outline" className="border-[#1e293b] text-[#94a3b8] hover:bg-[#1e293b]" asChild>
                                <span>
                                    <Upload className="w-4 h-4 mr-2" />
                                    {settings.logo_base64 ? 'Changer le logo' : 'Uploader un logo'}
                                </span>
                            </Button>
                        </label>
                        <p className="text-[#64748b] text-xs mt-2">PNG, JPG jusqu'à 5 MB</p>
                    </div>
                </div>

                {/* Banking Info */}
                <div className="stat-card">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 rounded-lg bg-green-400/10">
                            <CreditCard className="w-5 h-5 text-green-400" />
                        </div>
                        <h2 className="text-xl font-semibold text-[#f8fafc]">Informations bancaires</h2>
                    </div>

                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <Label className="text-[#94a3b8]">Nom de la banque</Label>
                                <Input
                                    data-testid="bank-name-input"
                                    value={settings.bank_name}
                                    onChange={(e) => setSettings({ ...settings, bank_name: e.target.value })}
                                    placeholder="Ex: Boursorama banque"
                                    className="mt-1 bg-[#020617] border-[#1e293b] text-[#f8fafc]"
                                />
                            </div>
                            <div>
                                <Label className="text-[#94a3b8]">Titulaire du compte</Label>
                                <div className="relative mt-1">
                                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#475569]" />
                                    <Input
                                        data-testid="account-holder-input"
                                        value={settings.account_holder}
                                        onChange={(e) => setSettings({ ...settings, account_holder: e.target.value })}
                                        placeholder="Nom du titulaire"
                                        className="pl-10 bg-[#020617] border-[#1e293b] text-[#f8fafc]"
                                    />
                                </div>
                            </div>
                        </div>
                        <div>
                            <Label className="text-[#94a3b8]">IBAN / RIB</Label>
                            <Input
                                data-testid="iban-input"
                                value={settings.iban}
                                onChange={(e) => setSettings({ ...settings, iban: e.target.value })}
                                placeholder="FR76 1234 5678 9012 3456 7890 123"
                                className="mt-1 bg-[#020617] border-[#1e293b] text-[#f8fafc] mono"
                            />
                        </div>
                        <div>
                            <Label className="text-[#94a3b8]">BIC</Label>
                            <Input
                                data-testid="bic-input"
                                value={settings.bic}
                                onChange={(e) => setSettings({ ...settings, bic: e.target.value })}
                                placeholder="BNPAFRPP XXX"
                                className="mt-1 bg-[#020617] border-[#1e293b] text-[#f8fafc] mono"
                            />
                        </div>
                    </div>
                </div>

                {/* Legal Mentions */}
                <div className="stat-card">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="p-2 rounded-lg bg-blue-400/10">
                            <FileText className="w-5 h-5 text-blue-400" />
                        </div>
                        <h2 className="text-xl font-semibold text-[#f8fafc]">Mentions légales</h2>
                    </div>

                    <div className="space-y-4">
                        <div>
                            <Label className="text-[#94a3b8]">Mentions légales - Devis</Label>
                            <Textarea
                                data-testid="legal-quote-input"
                                value={settings.legal_mentions_quote}
                                onChange={(e) => setSettings({ ...settings, legal_mentions_quote: e.target.value })}
                                placeholder="Pour toute commande un acompte de 30% vous sera demandé..."
                                className="mt-1 bg-[#020617] border-[#1e293b] text-[#f8fafc] min-h-[100px]"
                            />
                        </div>
                        <div>
                            <Label className="text-[#94a3b8]">Mentions légales - Factures</Label>
                            <Textarea
                                data-testid="legal-invoice-input"
                                value={settings.legal_mentions_invoice}
                                onChange={(e) => setSettings({ ...settings, legal_mentions_invoice: e.target.value })}
                                placeholder="Paiement à réception de facture..."
                                className="mt-1 bg-[#020617] border-[#1e293b] text-[#f8fafc] min-h-[100px]"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
